import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, Copy, Share2 } from "lucide-react";
import { formatDuration } from "./formatDuration";

export interface ShareMenuProps {
  /** Live media currentTime in seconds, lifted from WatchPlayer. */
  currentTime: number;
  /** Active fragment id — always written as ?r= so a ?t= resolves correctly. */
  fragmentId: string;
}

/**
 * YouTube-style share affordance for the public /watch page. A "Share" button
 * opens a popover with a "Start at {mm:ss}" checkbox and a Copy button.
 *
 * The {mm:ss} clock reflects the `currentTime` prop, which WatchPlayer lifts
 * from the <video> via timeupdate — so it ticks while playing and freezes when
 * paused (timeupdate does not fire while paused). With the checkbox on, the
 * copied URL carries ?t=<floor(seconds)> alongside the active ?r=; existing
 * params (e.g. utm_source) are preserved.
 *
 * Modeled on VideoControlBar's SettingsMenu: trigger button + container-ref-
 * guarded outside-click close + Escape-to-close with focus return.
 */
export function ShareMenu({ currentTime, fragmentId }: ShareMenuProps) {
  const { t } = useTranslation("pages");
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [startAt, setStartAt] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildShareUrl = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set("r", fragmentId);
    if (startAt) {
      params.set("t", String(Math.floor(currentTime)));
    } else {
      params.delete("t");
    }
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }, [searchParams, fragmentId, startAt, currentTime]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(buildShareUrl()).catch(() => undefined);
    setCopied(true);
    if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
  }, [buildShareUrl]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  const timeLabel = formatDuration(currentTime) ?? "0:00";

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-white/70 transition-colors hover:bg-white/5"
      >
        <Share2 className="h-3.5 w-3.5" aria-hidden="true" />
        {t("watch.share")}
      </button>
      {isOpen && (
        <div
          role="menu"
          className="absolute bottom-full right-0 mb-2 w-56 rounded-lg border border-[#334155] bg-[#020617]/95 p-3 shadow-lg backdrop-blur"
        >
          <label className="flex cursor-pointer items-center gap-2 text-sm text-white/80">
            <input
              type="checkbox"
              checked={startAt}
              onChange={(event) => setStartAt(event.target.checked)}
              className="h-4 w-4 accent-[#60a5fa]"
            />
            {t("watch.shareStartAt", { time: timeLabel })}
          </label>
          <button
            type="button"
            onClick={handleCopy}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-sm text-white/80 transition-colors hover:bg-white/5"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {copied ? t("watch.linkCopied") : t("watch.copyLink")}
          </button>
        </div>
      )}
    </div>
  );
}

export default ShareMenu;
